import { FC, HTMLAttributeAnchorTarget, MouseEvent, ReactNode, useEffect, useMemo } from 'react';

import { Button, ButtonProps } from '@mui/material';

import { isNationalCallCenterPhone, swapNationalPhone } from '@aspendental/web-shared-utils';

import { addClassName, dataLayerConfig, replaceUrlContentVars, swapPhoneNumberInChildren } from '@/utils';
import { useAppContext, useFacilityContext, useOfficeLocationContext } from '@/context';
import { MOTTO_ALIGNERS_PATH, SCHEDULE_PATH } from '@/constants';
import { Maybe, AnalyticsContext } from '@/types/generated';
import { getInnerTextForAnalytics } from '@/utils/getInnerTextForAnalytics/getInnerTextForAnalytics';

export interface ITAGButton extends ButtonProps {
	target?: HTMLAttributeAnchorTarget;
	dataTestId?: string;
	analyticsContext?: Maybe<AnalyticsContext>;
	component?: React.ElementType;
}

const getRegularScheduleHref = (href: string, pathname: string, facilityCode?: string): string => {
	const isSpanishDirectory = pathname.includes('espanol');
	const hasTrailingSlash = href.endsWith('/');
	const slashString = hasTrailingSlash ? '' : '/';
	const localizedHref = isSpanishDirectory && !href.includes('es-US') ? `${href}${slashString}es-US/` : href;
	return facilityCode && !localizedHref.includes('fc') ? `${localizedHref}?fc=${facilityCode}` : localizedHref;
};

const getMottoScheduleHref = (href: string, facilityCode?: string): string => {
	// If the URL already contains the mottoaligners path, return it as-is
	if (href.includes(`/${MOTTO_ALIGNERS_PATH}`)) return href;
	const transformedHref = href.endsWith('/') ? href.slice(0, -1) : href;
	return facilityCode && !href.includes('fc')
		? `${transformedHref}/${MOTTO_ALIGNERS_PATH}?fc=${facilityCode}`
		: `${transformedHref}/${MOTTO_ALIGNERS_PATH}`;
};

const getHref = (pathname: string, href: string, facilityCode?: string) => {
	const isUserOnMottoAligners =
		pathname.includes(MOTTO_ALIGNERS_PATH) || globalThis?.location?.hostname?.includes('mottoclearaligners');
	const isHrefForSchedule = href.includes(SCHEDULE_PATH);
	if (!isHrefForSchedule) return href;
	if (isUserOnMottoAligners) return getMottoScheduleHref(href, facilityCode);
	return getRegularScheduleHref(href, pathname, facilityCode);
};

const getLabel = (children: ReactNode, pathname: string, href: string | undefined) => {
	const isSpanishDirectory = pathname.includes('espanol');
	const isHrefForSchedule = href?.includes(SCHEDULE_PATH);
	if (isHrefForSchedule && isSpanishDirectory) {
		return <span>Haz una cita</span>;
	}
	return children;
};

const toSxArray = (sx: ButtonProps['sx']): Exclude<ButtonProps['sx'], undefined>[] => {
	if (!sx) return [];
	return Array.isArray(sx) ? sx : [sx];
};

const TAGButton: FC<ITAGButton> = ({
	children,
	dataTestId,
	href,
	analyticsContext,
	onClick,
	variant,
	sx,
	...restProps
}) => {
	const { facilityCode, officeInfo } = useFacilityContext();
	const { officeInfo: officeLocationInfo } = useOfficeLocationContext();
	const { config, phoneSwapOn } = useAppContext();
	if (href) {
		href = replaceUrlContentVars(href, officeInfo, officeLocationInfo, config);
	}

	// VWO `ccPatientPrismPhoneSwap`: swap the national call-center number (href +
	// label) for the Patient Prism pool number so PP swaps it for a dynamic
	// tracking number instead of Invoca. Facility numbers are never swapped.
	const isTelLink = !!href?.startsWith('tel:');
	const swapToPatientPrism = isTelLink && !!phoneSwapOn && isNationalCallCenterPhone(href ?? '');

	const pathname = globalThis.location?.pathname ?? '';
	const swappedChildren = swapToPatientPrism ? swapPhoneNumberInChildren(children) : children;
	const finalChildren = getLabel(swappedChildren, pathname, href);

	const updatedHref = useMemo(() => {
		if (!href) {
			return undefined;
		}
		if (swapToPatientPrism) {
			return swapNationalPhone(href, true);
		}
		return getHref(pathname, href, facilityCode);
	}, [href, facilityCode, pathname, swapToPatientPrism]);

	const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
		if (analyticsContext?.event) {
			dataLayerConfig({
				...analyticsContext,
				link_url: analyticsContext.linkUrl ?? updatedHref,
				page_destination_url: analyticsContext.linkUrl ?? updatedHref ?? '',
				website_selected_facility_id: officeInfo?.code ?? '',
				website_selected_facility_name: officeInfo?.name ?? '',
			});
		}

		if (config.name === 'ClearChoice' && analyticsContext?.event === 'mapbox_search_selection') {
			// default click_to_scheduler event
			dataLayerConfig({
				event: 'click_to_scheduler',
				brand: config.name,
				page_destination_url: updatedHref ?? '',
				click_name: getInnerTextForAnalytics(finalChildren),
				click_position: analyticsContext?.clickPosition ?? 'page',
				website_selected_facility_id: officeInfo?.code ?? '',
				website_selected_facility_name: officeInfo?.name ?? '',
			});
		} else if (analyticsContext?.event !== 'navigation_click_event') {
			// default data layer push regardless of analyticsContext existence
			dataLayerConfig({
				event: 'navigation_click_event',
				brand: config.name,
				page_destination_url: updatedHref ?? '',
				link_click_location: analyticsContext?.clickPosition ?? 'page',
				link_text: getInnerTextForAnalytics(finalChildren),
				website_selected_facility_id: officeInfo?.code ?? '',
				website_selected_facility_name: officeInfo?.name ?? '',
			});
		}

		if (onClick) {
			onClick(e);
		}

		return undefined;
	};

	useEffect(() => {
		if (!swapToPatientPrism && updatedHref?.startsWith('tel')) {
			dataLayerConfig({
				event: 'invoca_page_load',
				brand: config.name,
				website_selected_facility_id: officeInfo?.code || '',
				website_selected_facility_name: officeInfo?.name || '',
				invoca_phone_number: updatedHref
					.replace(/\D/g, '')
					.replace(/^1/, '')
					.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3'),
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updatedHref]);

	return (
		<Button
			{...restProps}
			variant={variant}
			href={updatedHref ?? href}
			className={swapToPatientPrism ? '' : addClassName(href)}
			onClick={(e) => handleClick(e)}
			data-testid={dataTestId}
			sx={
				toSxArray(sx).filter((sxEntry): sxEntry is Exclude<ButtonProps['sx'], undefined> =>
					Boolean(sxEntry)
				) as ButtonProps['sx']
			}
		>
			{finalChildren}
		</Button>
	);
};

export default TAGButton;
